class ParallaxScroll {
  constructor(options = {}) {
    this.options = {
      selector: options.selector || '[data-parallax]',
      speed: options.speed || 0.5,
      direction: options.direction || 'up',
      smooth: options.smooth || 0.1,
      autoRange: options.autoRange || 0.3,
    };

    this.elements = [];
    this.rafId = null;
    this.lastScrollY = window.scrollY;
    this.targetScrollY = window.scrollY;

    this.init();
  }

  /**
   * Inicializa la librería
   */
  init() {
    this.getElements();
    this.setupElements();
    this.bindEvents();
    this.startAnimation();
  }

  /**
   * Obtiene todos los elementos que tendrán parallax
   */
  getElements() {
    const nodes = document.querySelectorAll(this.options.selector);

    this.elements = Array.from(nodes).map(element => {
      const data = element.dataset;

      return {
        element,
        speed: parseFloat(data.parallaxSpeed) || this.options.speed,
        direction: data.parallaxDirection || this.options.direction,
        range: parseFloat(data.parallaxRange) || null,
        smooth: parseFloat(data.parallaxSmooth) || this.options.smooth,
        offset: parseFloat(data.parallaxOffset) || 0,
        currentY: 0,
        targetY: 0,
        initialTop: 0,
      };
    });
  }

  /**
   * Configura los estilos iniciales de los elementos
   */
  setupElements() {
    this.elements.forEach(item => {
      const rect = item.element.getBoundingClientRect();
      item.initialTop = rect.top + window.scrollY;

      item.element.style.willChange = 'transform';
      item.element.style.transform = `translate3d(0, ${item.offset}px, 0)`;
    });
  }

  /**
   * Vincula los eventos necesarios
   */
  bindEvents() {
    this.handleScroll = this.onScroll.bind(this);
    this.handleResize = this.onResize.bind(this);

    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Handler del evento scroll
   */
  onScroll() {
    this.targetScrollY = window.scrollY;
  }

  /**
   * Handler del evento resize
   */
  onResize() {
    this.setupElements();
    this.calculate();
  }

  /**
   * Calcula las posiciones de parallax
   */
  calculate() {
    this.lastScrollY += (this.targetScrollY - this.lastScrollY) * this.options.smooth;

    this.elements.forEach(item => {
      const elementTop = item.initialTop;
      const scrolled = this.lastScrollY;
      const viewportHeight = window.innerHeight;

      // Calcular visibilidad del elemento
      const elementRect = item.element.getBoundingClientRect();
      const elementBottom = elementTop + elementRect.height;

      // Rango de scroll donde el elemento está visible (con márgenes)
      const startScroll = elementTop - viewportHeight;
      const endScroll = elementBottom;
      const scrollRange = endScroll - startScroll;

      // Calcular progreso (0 a 1) basado en la visibilidad
      const progress = Math.max(0, Math.min(1, (scrolled - startScroll) / scrollRange));

      // Determinar el rango de movimiento
      const movementRange = item.range !== null
        ? item.range
        : viewportHeight * this.options.autoRange;

      // Calcular desplazamiento base: de -range a +range basado en el progreso
      let displacement = (progress - 0.5) * 2 * movementRange * item.speed;

      // Aplicar dirección (por defecto 'up': cuando bajas, elemento sube)
      if (item.direction === 'up') {
        displacement = -displacement; // Invertir: scroll down → elemento up (parallax clásico)
      }
      // Si direction === 'down': scroll down → elemento down (mantener signo original)

      // Aplicar offset inicial
      displacement += item.offset;

      // Smooth individual
      item.currentY += (displacement - item.currentY) * item.smooth;
      item.targetY = displacement;
    });
  }

  /**
   * Renderiza las transformaciones
   */
  render() {
    this.elements.forEach(item => {
      item.element.style.transform = `translate3d(0, ${item.currentY}px, 0)`;
    });
  }

  /**
   * Loop de animación
   */
  animate() {
    this.calculate();
    this.render();
    this.rafId = requestAnimationFrame(() => this.animate());
  }

  /**
   * Inicia la animación
   */
  startAnimation() {
    if (!this.rafId) {
      this.animate();
    }
  }

  /**
   * Pausa la animación
   */
  pause() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /**
   * Reanuda la animación
   */
  resume() {
    this.startAnimation();
  }

  /**
   * Actualiza elementos (útil para contenido dinámico)
   */
  refresh() {
    this.pause();
    this.getElements();
    this.setupElements();
    this.startAnimation();
  }

  /**
   * Destruye la instancia
   */
  destroy() {
    this.pause();
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);

    this.elements.forEach(item => {
      item.element.style.transform = '';
      item.element.style.willChange = '';
    });

    this.elements = [];
  }
}

// Export para módulos ES6
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ParallaxScroll;
}

// Export para AMD (RequireJS)
if (typeof define === 'function' && define.amd) {
  define([], function () {
    return ParallaxScroll;
  });
}

export { ParallaxScroll }
